/**
 * 참가비 회차 — 요약 한 줄과 날짜별 묶음.
 * @description 앱 `toSessionSummary` · `groupSessionsByDate` 와 같은 규칙이다. 회차 수는 취소되지 않은
 *   회차만 센다. 예약할 수 있는 회차(취소 · 마감 · 꽉 참이 아닌 것)가 없으면 수만 알린다.
 *   날짜는 표시 시간대 안에서 묶는다 — 시간대가 바뀌면 묶음도 바뀐다.
 */
import { formatClock, formatDay, formatDayTime } from "./collabV3Format";
import { COLLAB_V3_CARD_COPY } from "../constants/collabV3CardCopy";
import type { CollabV3FeeSession } from "../entities";

export interface CollabV3SessionGroup {
  key: string;
  label: string;
  rows: { session: CollabV3FeeSession; time: string }[];
}

const copy = COLLAB_V3_CARD_COPY.fee.sessions;

const isBookable = (session: CollabV3FeeSession): boolean =>
  !session.isCancelled && !session.isClosed && session.remainingSeats > 0;

/** `2 sessions • next 16 October 18:00 • 9 seats left` */
export function summarizeSessions(
  sessions: CollabV3FeeSession[],
  timeZone: string,
): string {
  const count = sessions.filter((session) => !session.isCancelled).length;
  const bookable = sessions.filter(isBookable);
  const next = bookable[0];
  if (!next) return copy.summaryNone(count);
  const seats = bookable.reduce(
    (sum, session) => sum + session.remainingSeats,
    0,
  );
  return copy.summary(count, formatDayTime(next.startAt, timeZone), seats);
}

/** 시작 순 그대로 날짜마다 묶는다 — 서버가 시작 순으로 준다 */
export function groupSessionsByDay(
  sessions: CollabV3FeeSession[],
  timeZone: string,
): CollabV3SessionGroup[] {
  const groups = new Map<string, CollabV3SessionGroup>();
  for (const session of sessions) {
    const label = formatDay(session.startAt, timeZone);
    const group = groups.get(label) ?? { key: label, label, rows: [] };
    const time = `${formatClock(session.startAt, timeZone)} – ${formatClock(session.endAt, timeZone)}`;
    group.rows.push({ session, time });
    groups.set(label, group);
  }
  return [...groups.values()];
}
